/**
 * MobileVoyage — RC8 Phase 1 Slice 4 · Multi-day collapsible itinerary.
 *
 * PO-ratified 2026-02-17 · consumes the canonical composed multi-day
 * projection:
 *
 *     GET /api/cruises/current/journey  ·  returns
 *       { voyage, overall, current_day_number, days[] }
 *     Each day carries events[] composed by the SAME canonical
 *     `day_composer.compose_day_events` used by `/today`. Every event
 *     has a nested `time_context = { instant_utc, context,
 *     instant_display }` per ADR-TIME-001.
 *
 * Architectural guardrails (all enforced at file level):
 *   · NO mobile-specific itinerary endpoint · consumes /journey only.
 *   · NO second event composer · presentation-only reads.
 *   · NO fabricated events · empty days render an editorial empty note.
 *   · NO second time authority · every time string derives from
 *     `event.time_context.instant_display` and `context`.
 *   · Session-sticky expansion state in sessionStorage · voyage-scoped
 *     key so a different voyage doesn't leak the wrong expansion state.
 *     Multiple days may be expanded simultaneously (no forced-single).
 *   · Current day (or the resolved "relevant" day when pre/post-voyage)
 *     is expanded on first render · gets a subtle TODAY treatment when
 *     the voyage is in-flight.
 *
 * Non-goals in this slice (explicit deferrals per PO):
 *   · No SQLite offline layer.
 *   · No device calendar export.
 *   · No push notifications, biometric-gated reveals, or SMS backstop.
 *   · No muster fixture fabrication when the voyage carries no verified
 *     `muster_drill_at` — the empty state is a valid rendered outcome.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChevronDown, ChevronRight, Anchor, MapPin, Utensils, Plane,
  Building2, Car, ParkingCircle, Compass, Info, AlertTriangle, CalendarDays,
  Plus, Sparkles, Music, User, Loader2, WifiOff,
} from "lucide-react";
import { useCruise } from "@/context/CruiseContext";
// RC8 Slice 6 · Offline foundation · read-through cache for the canonical
// multi-day journey projection. Backend remains authoritative — this
// wire-up is purely for offline read reliability. Never a second composer.
import { syncJourney, readCachedJourney } from "@/mobile/offline/sync";
// RC7-JOURNEY-UX-001 · canonical POST /api/cruises/current/experience-events
// writer (no mobile-only endpoint, no second authority).
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter, DialogClose,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#0F2244";
const GOLD = "#C28E42";

// Editorial label for a `time_context.context` value.
const CONTEXT_LABEL = {
  ship: "Ship Time",
  port: "Port Local",
  home: "Home Time",
  utc:  "UTC",
};

const KIND_ICON = {
  dining:                     Utensils,
  excursion:                  MapPin,
  flight_booking:             Plane,
  hotel_reservation:          Building2,
  ground_reservation:         Car,
  transportation_reservation: Car,
  rental_car:                 Car,
  parking_reservation:        ParkingCircle,
  voyage_event:               Anchor,
  // RC7-JOURNEY-UX-001 · traveller-created kinds emitted by the shared
  // day_composer from `cruise_experience_events`. Icons chosen to feel
  // personal / editorial and distinct from operator-sourced reservations.
  entertainment:              Music,
  activity:                   Sparkles,
  personal:                   User,
};

const DAY_TYPE_LABEL = {
  embark:          "EMBARKATION",
  disembark:       "DISEMBARKATION",
  port:            "PORT DAY",
  sea_day:         "AT SEA",
  private_island:  "PRIVATE ISLAND",
};

// ---------------------------------------------------------------------------
// Session storage · voyage-scoped, expansion state
// ---------------------------------------------------------------------------

const STORAGE_KEY_PREFIX = "cc-mobile-voyage-expanded-days";

function storageKey(voyageId) {
  return voyageId ? `${STORAGE_KEY_PREFIX}::${voyageId}` : STORAGE_KEY_PREFIX;
}

function readExpandedSet(voyageId) {
  if (typeof window === "undefined" || !window.sessionStorage) return null;
  try {
    const raw = window.sessionStorage.getItem(storageKey(voyageId));
    if (!raw) return null;
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return null;
    return new Set(arr.map(Number).filter((n) => Number.isFinite(n)));
  } catch {
    return null;
  }
}

function writeExpandedSet(voyageId, set) {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(
      storageKey(voyageId),
      JSON.stringify([...set].sort((a, b) => a - b)),
    );
  } catch {
    // Storage full / disabled — graceful fallback: in-memory state
    // remains authoritative for the render pass; we simply don't
    // persist. Do NOT throw — never break the Voyage surface.
  }
}

// ---------------------------------------------------------------------------
// Editorial pretty-printer for a day header date.
// ---------------------------------------------------------------------------

function formatDayHeaderDate(dateIso) {
  if (!dateIso) return "";
  try {
    // Use UTC so we don't drift by browser timezone. The date is a
    // calendar-date string (YYYY-MM-DD) not an instant.
    const [y, m, d] = dateIso.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    return dt.toLocaleDateString(undefined, {
      weekday: "long", month: "long", day: "numeric",
      timeZone: "UTC",
    }).toUpperCase();
  } catch {
    return dateIso;
  }
}

// ---------------------------------------------------------------------------
// Presentational subcomponents
// ---------------------------------------------------------------------------

function EventRow({ event }) {
  const Icon = KIND_ICON[event.kind] || Compass;
  const tc = event.time_context || {};
  const ctxLabel = CONTEXT_LABEL[tc.context] || "";
  return (
    <div
      className="flex items-start gap-3 rounded-2xl bg-white border border-slate-100 px-4 py-3 shadow-[0_1px_0_rgba(15,34,68,0.04)]"
      data-testid={`mobile-voyage-event-${event.id}`}
      data-event-kind={event.kind}
      data-event-context={tc.context || ""}
    >
      <div className="shrink-0 w-9 h-9 rounded-full bg-slate-50 flex items-center justify-center text-[#0F2244]">
        <Icon size={16} strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
          {tc.instant_display || event.time || "—"}
          {ctxLabel ? (
            <span
              className="ml-2 inline-block align-middle text-slate-400"
              data-testid={`mobile-voyage-event-${event.id}-context-label`}
            >
              · {ctxLabel}
            </span>
          ) : null}
        </div>
        <div className="mt-0.5 text-sm font-semibold text-slate-900 leading-snug">
          {event.title}
        </div>
        {event.location_from || event.location_to ? (
          <div className="text-xs text-slate-500 mt-0.5">
            {event.location_from}
            {event.location_to ? ` → ${event.location_to}` : ""}
          </div>
        ) : null}
        {event.confirmation_number ? (
          <div className="text-[10px] mt-0.5 text-slate-400">
            Confirmation · {event.confirmation_number}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function DayEmptyState() {
  return (
    <div
      className="rounded-2xl border border-dashed border-slate-200 bg-white/60 px-4 py-6 text-center"
      data-testid="mobile-voyage-day-empty"
    >
      <div className="text-sm font-medium text-slate-800">
        No confirmed plans yet.
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Confirmed dining, excursions, and travel will surface here when they exist.
      </p>
    </div>
  );
}


// ---------------------------------------------------------------------------
// RC7-JOURNEY-UX-001 · Add to my itinerary
//
// Small mobile affordance that writes a canonical
// `cruise_experience_events` row via the existing writer:
//
//     POST /api/cruises/current/experience-events
//
// The event then flows through the same shared composer that owns
// dining and excursions, so Journey / Today / What's Next / MobileVoyage /
// Offline pack all pick it up naturally. No second endpoint. No local-
// only records. No optimistic fake events.
// ---------------------------------------------------------------------------

function useOnlineStatus() {
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" && navigator.onLine !== false,
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

function AddItineraryDialog({ open, onOpenChange, defaultDate, onCreated }) {
  const [title, setTitle]           = useState("");
  const [time, setTime]             = useState("");
  const [venue, setVenue]           = useState("");
  const [notes, setNotes]           = useState("");
  const [pending, setPending]       = useState(false);
  const [error, setError]           = useState(null);

  // Reset local form state each time the dialog opens on a new day.
  useEffect(() => {
    if (open) {
      setTitle("");
      setTime("");
      setVenue("");
      setNotes("");
      setPending(false);
      setError(null);
    }
  }, [open, defaultDate]);

  const canSubmit = title.trim().length > 0 && !pending;

  const submit = useCallback(async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (!canSubmit) return;
    setPending(true);
    setError(null);
    try {
      const body = {
        // `kind: personal` per PO Section 1 — "feel like adding
        // something to the traveller's personal plan, not creating a
        // cruise-line reservation."
        kind: "personal",
        date: defaultDate,
        title: title.trim(),
      };
      const t = time.trim();
      if (t) body.time = t;
      const v = venue.trim();
      if (v) body.venue = v;
      const n = notes.trim();
      if (n) body.notes = n;
      const { data } = await api.post(
        "/api/cruises/current/experience-events",
        body,
      );
      toast.success("Added to your itinerary", {
        description: `${data.title} · ${data.date}${data.time ? " · " + data.time : ""}`,
      });
      onOpenChange(false);
      // Refresh Journey (canonical composed multi-day) — the new event
      // will surface on the correct day via the same composer path
      // used by /today, /journey, MobileToday, WhatsNextHero, and the
      // Offline pack. No local-only insertion.
      if (typeof onCreated === "function") onCreated(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setPending(false);
    }
  }, [canSubmit, defaultDate, title, time, venue, notes, onOpenChange, onCreated]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md max-h-[92vh] overflow-y-auto"
        data-testid="add-itinerary-dialog"
      >
        <DialogHeader>
          <DialogTitle data-testid="add-itinerary-title">
            Add to my itinerary
          </DialogTitle>
          <DialogDescription>
            Something you're planning yourself — a show, a walk on deck,
            a moment worth remembering. It'll appear on your day{" "}
            <span className="font-medium">{formatDayHeaderDate(defaultDate)}</span>.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="add-itinerary-title-input" className="text-xs uppercase tracking-[0.14em] text-slate-500">
              What are you planning?
            </Label>
            <Input
              id="add-itinerary-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Anniversary dinner, Hairspray, Sunset walk"
              maxLength={140}
              autoFocus
              required
              data-testid="add-itinerary-input-title"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="add-itinerary-time-input" className="text-xs uppercase tracking-[0.14em] text-slate-500">
                Time (optional)
              </Label>
              <Input
                id="add-itinerary-time-input"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="add-itinerary-input-time"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="add-itinerary-venue-input" className="text-xs uppercase tracking-[0.14em] text-slate-500">
                Venue (optional)
              </Label>
              <Input
                id="add-itinerary-venue-input"
                value={venue}
                onChange={(e) => setVenue(e.target.value)}
                placeholder="e.g. Royal Theater, Suite balcony"
                maxLength={140}
                data-testid="add-itinerary-input-venue"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="add-itinerary-notes-input" className="text-xs uppercase tracking-[0.14em] text-slate-500">
              Notes (optional)
            </Label>
            <Textarea
              id="add-itinerary-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything you want to remember."
              maxLength={1000}
              rows={3}
              data-testid="add-itinerary-input-notes"
            />
          </div>

          {error ? (
            <div
              className="rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-xs text-red-700 flex items-start gap-2"
              data-testid="add-itinerary-error"
            >
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          <DialogFooter className="gap-2 sm:gap-2">
            <DialogClose asChild>
              <button
                type="button"
                className="rounded-full px-4 py-2 text-sm font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition-colors"
                data-testid="add-itinerary-cancel"
              >
                Cancel
              </button>
            </DialogClose>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-full px-4 py-2 text-sm font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: NAVY }}
              data-testid="add-itinerary-submit"
            >
              {pending ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" />
                  Adding…
                </span>
              ) : (
                <span>Add to itinerary</span>
              )}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddItineraryTrigger({ dayDate, online, onCreated }) {
  const [open, setOpen] = useState(false);
  if (!dayDate) return null;
  return (
    <>
      <button
        type="button"
        disabled={!online}
        onClick={() => setOpen(true)}
        title={online
          ? "Add something you're planning yourself"
          : "Reconnect to add to your itinerary"}
        className="mt-1 w-full min-h-[44px] rounded-2xl border border-dashed border-slate-300 bg-white/60 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-white hover:border-slate-400 active:bg-slate-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={`mobile-voyage-add-itinerary-${dayDate}`}
        data-online={online ? "true" : "false"}
      >
        {online ? <Plus size={14} strokeWidth={2.5} /> : <WifiOff size={14} />}
        <span>
          {online ? "Add to my itinerary" : "Add to my itinerary · offline"}
        </span>
      </button>
      <AddItineraryDialog
        open={open}
        onOpenChange={setOpen}
        defaultDate={dayDate}
        onCreated={onCreated}
      />
    </>
  );
}

function DayAccordion({ day, isExpanded, isToday, onToggle, online, onCreated }) {
  const dayTypeLabel = DAY_TYPE_LABEL[day.day_type] || day.day_type || "";
  const eventsCount = (day.events || []).length;
  return (
    <div
      className="rounded-2xl bg-white border border-slate-100 overflow-hidden"
      data-testid={`mobile-voyage-day-${day.day_number}`}
      data-day-number={day.day_number}
      data-day-expanded={isExpanded ? "true" : "false"}
      data-day-today={isToday ? "true" : "false"}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full min-h-[56px] px-4 py-3 flex items-center gap-3 text-left active:bg-slate-50 transition-colors"
        aria-expanded={isExpanded}
        aria-controls={`mobile-voyage-day-${day.day_number}-panel`}
        data-testid={`mobile-voyage-day-${day.day_number}-header`}
      >
        {/* Day number rail */}
        <div className="shrink-0 w-11 flex flex-col items-center leading-none">
          <div className="text-[9px] font-medium tracking-[0.18em] text-slate-400">
            DAY
          </div>
          <div
            className="mt-0.5 font-display font-black text-xl"
            style={{ color: NAVY }}
          >
            {day.day_number}
          </div>
        </div>

        {/* Body */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500">
              {formatDayHeaderDate(day.date)}
            </span>
            {isToday ? (
              <span
                className="text-[9px] font-bold uppercase tracking-[0.2em] px-1.5 py-0.5 rounded-sm"
                style={{ background: GOLD, color: "white" }}
                data-testid={`mobile-voyage-day-${day.day_number}-today-badge`}
              >
                Today
              </span>
            ) : null}
          </div>
          <div className="mt-1 flex items-baseline gap-2 flex-wrap">
            <div className="text-base font-semibold text-slate-900 truncate">
              {day.port_name || (day.day_type === "sea_day" ? "At Sea" : "—")}
            </div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">
              {dayTypeLabel}
            </div>
          </div>
          <div className="mt-1 text-[11px] text-slate-500">
            {eventsCount === 0
              ? "No confirmed events"
              : `${eventsCount} event${eventsCount === 1 ? "" : "s"}`}
          </div>
        </div>

        {/* Chevron */}
        <div className="shrink-0 text-slate-400" aria-hidden="true">
          {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </div>
      </button>

      {isExpanded ? (
        <div
          id={`mobile-voyage-day-${day.day_number}-panel`}
          className="border-t border-slate-100 bg-slate-50/60 px-4 py-3 space-y-2"
          data-testid={`mobile-voyage-day-${day.day_number}-body`}
        >
          {eventsCount === 0
            ? <DayEmptyState />
            : (day.events || []).map((e) => (
                <EventRow key={e.id} event={e} />
              ))}
          <AddItineraryTrigger
            dayDate={day.date}
            online={online}
            onCreated={onCreated}
          />
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root component
// ---------------------------------------------------------------------------

function resolveDefaultExpandedDay(payload) {
  if (!payload || !Array.isArray(payload.days) || !payload.days.length) return null;
  // Priority 1 · current voyage day (from server, never inferred client-side)
  if (payload.current_day_number) {
    const match = payload.days.find((d) => d.day_number === payload.current_day_number);
    if (match) return match.day_number;
  }
  // Priority 2 · pre-voyage → first day (embark)
  if (payload.overall === "pre-voyage") return payload.days[0].day_number;
  // Priority 3 · post-voyage → last day (disembark)
  if (payload.overall === "post-voyage") {
    return payload.days[payload.days.length - 1].day_number;
  }
  // Priority 4 · fallback → first day.
  return payload.days[0].day_number;
}

export default function MobileVoyage() {
  const { cruise } = useCruise();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  // RC8 Slice 6 · offline read-through cache metadata.
  const [cacheSource, setCacheSource] = useState(null);  // "network" | "cache" | "empty"
  // RC7-JOURNEY-UX-001 · online status drives the "+ Add to my itinerary"
  // affordance disabled-state. Offline creation is not supported by the
  // existing architecture (no outbox writer for experience_events yet),
  // so we honestly disable the action instead of fabricating a fake
  // local-only event.
  const online = useOnlineStatus();

  const voyageId = payload?.voyage?.id || cruise?.id || null;

  // Fetch multi-day journey composition · canonical endpoint.
  // RC8 Slice 6 · reads through the offline cache: cache-first paint
  // (if available) → network refresh → persist. Backend authoritative.
  const loadJourney = useCallback(async ({ networkOnly = false } = {}) => {
    try {
      if (!networkOnly) setLoading(true);
      setErr(null);
      // 1) Fast paint from cache when we already have both scopes.
      if (!networkOnly && cruise?.owner_id && cruise?.id) {
        const cached = await readCachedJourney({
          user_id: cruise.owner_id, voyage_id: cruise.id,
        });
        if (cached) {
          setPayload(cached);
          setCacheSource("cache");
          setLoading(false);
        }
      }
      // 2) Network refresh + persist.
      const { data, meta } = await syncJourney({
        user_id: cruise?.owner_id || null,
        voyage_id: cruise?.id || null,
      });
      if (data) {
        setPayload(data);
        setCacheSource(meta.source);
      } else if (meta.source === "empty" && !networkOnly) {
        // Offline + no cache — surface the error state.
        setErr(meta.error || "offline_no_cache");
      }
    } catch (e) {
      setErr(String(e));
    } finally {
      setLoading(false);
    }
  }, [cruise?.owner_id, cruise?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await loadJourney();
    })();
    return () => { cancelled = true; };
  }, [loadJourney]);

  // Initialise expansion set once payload arrives. sessionStorage wins
  // when present; otherwise fall back to the resolved default day.
  useEffect(() => {
    if (!payload) return;
    const stored = readExpandedSet(voyageId);
    if (stored && stored.size) {
      setExpanded(stored);
      return;
    }
    const defaultDay = resolveDefaultExpandedDay(payload);
    setExpanded(defaultDay ? new Set([defaultDay]) : new Set());
  }, [payload, voyageId]);

  // Persist expansion state whenever it changes (voyage-scoped key).
  useEffect(() => {
    if (!payload || !voyageId) return;
    writeExpandedSet(voyageId, expanded);
  }, [expanded, voyageId, payload]);

  const toggleDay = useCallback((dayNumber) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(dayNumber)) next.delete(dayNumber);
      else next.add(dayNumber);
      return next;
    });
  }, []);

  const days = payload?.days || [];
  const shipName = payload?.voyage?.ship_name || cruise?.ship_name || "Your voyage";
  const totalDays = payload?.voyage?.total_days || days.length;
  const dateRange = useMemo(() => {
    if (!payload?.voyage) return "";
    const s = payload.voyage.embark_date;
    const e = payload.voyage.disembark_date;
    if (!s || !e) return "";
    return `${s} → ${e}`;
  }, [payload]);

  const currentDayNumber = payload?.current_day_number || null;

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-28"
      data-testid="mobile-voyage-root"
    >
      {/* Editorial header */}
      <div
        className="px-5 pt-6 pb-5 text-white rounded-b-3xl"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #14315e 100%)` }}
        data-testid="mobile-voyage-header"
      >
        <div className="text-[11px] uppercase tracking-[0.2em] text-white/70">
          Voyage
        </div>
        <h1
          className="mt-1 font-display font-black tracking-tight text-white"
          style={{ fontSize: "clamp(22px, 5.6vw, 28px)", lineHeight: 1.05 }}
          data-testid="mobile-voyage-title"
        >
          {shipName}
        </h1>
        <div className="mt-1 text-sm text-white/80 flex items-center gap-2">
          <CalendarDays size={13} className="opacity-80" />
          <span>{totalDays} day{totalDays === 1 ? "" : "s"}</span>
          {dateRange ? <span className="text-white/50">·</span> : null}
          {dateRange ? <span className="tabular-nums">{dateRange}</span> : null}
        </div>
      </div>

      {/* RC8 Slice 6 · offline / stale-cache indicator. Only renders when
          the current paint came from the local C2 cache rather than a
          fresh network fetch. Editorial, never alarming. */}
      {cacheSource === "cache" && !loading ? (
        <div
          className="mx-4 mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800 flex items-center gap-2"
          data-testid="mobile-voyage-offline-banner"
        >
          <Info size={13} className="shrink-0" />
          <span>Showing your last-synced voyage. Reconnect for the latest.</span>
        </div>
      ) : null}

      {/* Body */}
      <div className="px-4 mt-5 space-y-3" data-testid="mobile-voyage-days">
        {loading ? (
          <>
            {[1, 2, 3, 4].map((k) => (
              <div
                key={k}
                className="rounded-2xl border border-slate-100 bg-white h-[76px] animate-pulse"
                data-testid="mobile-voyage-day-skeleton"
              />
            ))}
          </>
        ) : err ? (
          <div
            className="rounded-2xl bg-red-50 p-4 text-sm text-red-700 border border-red-100 flex items-start gap-2"
            data-testid="mobile-voyage-error"
          >
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <div>
              {String(err).includes("offline_no_cache")
                ? "Connect once to download your voyage information."
                : "Couldn't load your voyage itinerary. Pull to refresh."}
            </div>
          </div>
        ) : !days.length ? (
          <div
            className="mt-6 rounded-2xl border border-dashed border-slate-200 bg-white/60 px-5 py-8 text-center"
            data-testid="mobile-voyage-empty-state"
          >
            <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-[#0F2244]">
              <Info size={22} />
            </div>
            <div className="mt-3 text-base font-semibold text-slate-900">
              No voyage days on file.
            </div>
            <p className="mt-1 text-sm text-slate-600 leading-relaxed">
              When your itinerary is confirmed, every day of your cruise will
              appear here.
            </p>
          </div>
        ) : (
          days.map((day) => (
            <DayAccordion
              key={day.day_number}
              day={day}
              isExpanded={expanded.has(day.day_number)}
              isToday={currentDayNumber === day.day_number}
              onToggle={() => toggleDay(day.day_number)}
              online={online}
              onCreated={() => loadJourney({ networkOnly: true })}
            />
          ))
        )}
      </div>
    </div>
  );
}

// Test-only helpers surfaced for the source-code guard suite.
export {
  resolveDefaultExpandedDay, storageKey,
  readExpandedSet, writeExpandedSet,
};
